import { flatBasis, viewDir } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyImpulse, applyStatus, explode, hitscan, isEnemy, playersInRadius, spawnZone } from "../../sim/combat.ts";
import { spawnConstruct } from "../../sim/constructs.ts";
import { FxKind } from "../../sim/events.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, fxk, zk, type HeroModule } from "../module.ts";
import { action, commonInput, constructAimAt, constructHitscan, consume, groundAimPoint, groundInFront, ready, secs, spreadDir, startMove, trigger, useUlt } from "../kitlib.ts";

export const V = { mode: 0, turretNet: 1, ultHeat: 2, mounted: 3 } as const;
export const CK = { Turret: ck(HeroId.Punnisher, 0) } as const;
/** Height of the mounted turret's barrel above its base. */
const TURRET_MUZZLE = 0.9;
export const ZK = { Smoke: zk(HeroId.Punnisher, 0) } as const;
export const FX = { Rifle: fxk(HeroId.Punnisher, 0), Shotgun: fxk(HeroId.Punnisher, 1), Turret: fxk(HeroId.Punnisher, 2), Smoke: fxk(HeroId.Punnisher, 3) } as const;

const def: HeroDef = {
  id: HeroId.Punnisher,
  slug: "the-punnisher",
  name: "The Punnisher",
  role: Role.Damage,
  color: "#bfc4cc",
  blurb: "A grim arsenal duelist who swaps rifle and shotgun, grapples to sightlines, and locks down lanes with a mounted turret.",
  health: 300,
  armor: 0,
  shields: 0,
  radius: 0.45,
  height: 1.9,
  headRadius: 0.24,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 40,
  reloadTime: 1.7,
  weapons: [
    { name: "Sentence Sprayer", ammo: 40, reloadTime: 1.7 },
    { name: "Rebuttal Scattergun", ammo: 8, reloadTime: 1.7 },
  ],
  ultCost: 3400,
  abilities: {
    [Slot.Primary]: { name: "Sentence Sprayer", key: "LMB", description: "Fire the rifle or the shotgun. 1 and 2 or the mouse wheel switch between them.", cooldown: 0 },
    [Slot.Secondary]: { name: "Smoke Rebuttal", key: "RMB", description: "Throw a smoke grenade that damages enemies and kicks you backward.", cooldown: 8 },
    [Slot.Ability1]: { name: "Clause Cable", key: "Shift", description: "Grapple to terrain in front of you and zip toward the hook point.", cooldown: 20 },
    [Slot.Ability2]: { name: "Mounted Argument", key: "E", description: "Deploy or mount a turret. While mounted, you are rooted and fire its gatling.", cooldown: 20 },
    [Slot.Ultimate]: { name: "Final Punchline", key: "Q", description: "For 10 seconds, unleash rapid piercing gunfire and missile bursts.", cooldown: 0 },
  },
  passive: { name: "Last Seen Clause", description: "Damaging enemies briefly marks them for your team." },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.mode, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      commonInput(sim, eid, input, pressed);
      // The weapon in hand, for the client's gun models and the bot.
      setHv(c, eid, V.mode, c.Weapon.slot[eid]!);
      const mountedTurret = turretForNet(sim, hv(c, eid, V.turretNet));
      if (hv(c, eid, V.mounted) > 0 && mountedTurret === undefined) setHv(c, eid, V.mounted, 0);
      if (c.Ult.active[eid]! > 0) setHv(c, eid, V.ultHeat, Math.min(100, hv(c, eid, V.ultHeat) + 50 / 60));
      else setHv(c, eid, V.ultHeat, Math.max(0, hv(c, eid, V.ultHeat) - 75 / 60));
      if (!canAct) return;

      if (pressed & Btn.Ability2 && hv(c, eid, V.mounted) > 0) {
        setHv(c, eid, V.mounted, 0);
        action(sim, eid, ActionKind.Cast, 8);
        return;
      }
      if (pressed & Btn.Ability2 && mountedTurret !== undefined && dist2d(sim, eid, mountedTurret) < 2.6) {
        setHv(c, eid, V.mounted, 1);
        action(sim, eid, ActionKind.Cast, 8);
        return;
      }
      if (hv(c, eid, V.mounted) > 0 && mountedTurret !== undefined) {
        c.Construct.yaw[mountedTurret] = input.yaw;
        const tp = sim.posOf(mountedTurret);
        const { fwd } = flatBasis(input.yaw);
        c.Pos.x[eid] = tp.x - fwd.x * 0.9;
        c.Pos.z[eid] = tp.z - fwd.z * 0.9;
        if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
          c.Weapon.fireCd[eid] = 5;
          action(sim, eid, ActionKind.Fire, 4);
          const a = aim(sim, eid);
          const seed = c.Weapon.shots[eid]!;
          // From the turret's barrel to what the crosshair is on, not parallel to the eye's aim.
          const h = TURRET_MUZZLE;
          const hit = constructHitscan(sim, mountedTurret, spreadDir(constructAimAt(sim, mountedTurret, a.point, h), seed > 20 ? 0.005 : seed > 10 ? 0.01 : 0.02, seed * 31), {
            range: 60,
            damage: 10,
            fxKind: FX.Turret,
            height: h,
          });
          if (hit.target !== undefined) explode(sim, eid, hit.end, { radius: 3, damage: 10, minDamage: 10, fxKind: FX.Turret, skip: hit.target });
        }
        return;
      }

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(10);
        setHv(c, eid, V.ultHeat, 0);
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        const a = aim(sim, eid, 35, false);
        const d = Math.hypot(a.point.x - a.origin.x, a.point.y - a.origin.y, a.point.z - a.origin.z);
        if (d >= 5) {
          consume(sim, eid, Slot.Ability1);
          startMove(sim, eid, MoveKind.Grapple, secs(1.4), 25, a.point);
          action(sim, eid, ActionKind.Cast, 16);
        }
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 16);
        const pos = groundInFront(sim, eid, input.yaw, 1.15, 0.9);
        const t = spawnConstruct(sim, eid, CK.Turret, pos, input.yaw, { seconds: 16 });
        if (t !== undefined) setHv(c, eid, V.turretNet, c.Net.id[t]!);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.Cast, 12);
        const pos = groundAimPoint(sim, eid, 18, 0.25);
        spawnZone(sim, eid, ZK.Smoke, pos, 5, 4);
        explode(sim, eid, pos, { radius: 5, damage: 45, knockback: 5, fxKind: FX.Smoke });
        const back = viewDir(input.yaw + Math.PI, 0);
        applyImpulse(sim, eid, { x: back.x * 9, y: 3.2, z: back.z * 9 });
      }

      if (c.Ult.active[eid]! > 0) {
        if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
          c.Weapon.fireCd[eid] = 2;
          action(sim, eid, ActionKind.Fire, 4);
          const a = aim(sim, eid);
          const seed = c.Weapon.shots[eid]!;
          hitscan(sim, eid, a.origin, spreadDir(a.dir, seed > 100 ? 0.015 : seed > 50 ? 0.03 : 0.06, seed * 31), {
            range: 70,
            damage: 8,
            falloffStart: 20,
            falloffEnd: 40,
            falloffMin: 0.7,
            canCrit: true,
            fxKind: FX.Turret,
          });
          if (seed % 100 === 0) {
            for (const { eid: t } of playersInRadius(sim, a.point, 3, (e) => isEnemy(sim, eid, e))) applyDamage(sim, t, 50, eid, { pos: a.point });
            sim.emit({ type: "explosion", pos: a.point, radius: 3, kind: FxKind.Missile });
          }
        }
        return;
      }

      const shotgun = hv(c, eid, V.mode) > 0.5;
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        const a = aim(sim, eid);
        if (shotgun) {
          c.Weapon.fireCd[eid] = 42;
          action(sim, eid, ActionKind.Fire, 14);
          for (let i = 0; i < 16; i++)
            hitscan(sim, eid, a.origin, spreadDir(a.dir, 0.06, c.Weapon.shots[eid]! * 19 + i * 7), {
              range: 35,
              damage: 9.5,
              falloffStart: 8,
              falloffEnd: 15,
              falloffMin: 0.4,
              canCrit: true,
              fxKind: FX.Shotgun,
              tracer: i < 3,
            });
        } else {
          c.Weapon.fireCd[eid] = 6;
          action(sim, eid, ActionKind.Fire, 5);
          hitscan(sim, eid, a.origin, spreadDir(a.dir, 0.0075, c.Weapon.shots[eid]! * 13), {
            range: 70,
            damage: 19,
            falloffStart: 20,
            falloffEnd: 40,
            falloffMin: 0.85,
            canCrit: true,
            fxKind: FX.Rifle,
          });
        }
      }
    },
    movement(sim, eid, _input, mods) {
      if (hv(sim.c, eid, V.mounted) > 0) {
        mods.speedMul = 0;
        mods.canJump = false;
      }
      if (sim.c.Ult.active[eid]! > 0) mods.speedMul *= sim.c.Weapon.action[eid] === ActionKind.Fire ? 0.7 : 0.9;
    },
    onDealtDamage(sim, eid, target) {
      applyStatus(sim, target, "marked", 3, { source: eid, by: eid });
    },
  },
  constructs: {
    [CK.Turret]: {
      maxHp: 600,
      seconds: 16,
      shape: { kind: "box", hw: 0.8, hd: 0.65, h: 1.1 },
      solid: true,
      blocksShots: true,
      onDestroy(sim, e) {
        const owner = sim.c.Construct.owner[e]!;
        if (sim.players.has(owner) && hv(sim.c, owner, V.turretNet) === sim.c.Net.id[e]) {
          setHv(sim.c, owner, V.turretNet, 0);
          setHv(sim.c, owner, V.mounted, 0);
        }
      },
      visual: { color: "#bfc4cc", shape: "box" },
    },
  },
  zones: {
    [ZK.Smoke]: {
      visual: { color: "#6f747d", dome: true },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        for (const { eid: t } of playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]!, (e) => isEnemy(sim, owner, e), false)) applyStatus(sim, t, "slow", 0.25, { slowPct: 0.25, source: owner });
      },
    },
  },
  fx: {
    [FX.Rifle]: { color: "#d7dde8" },
    [FX.Shotgun]: { color: "#f2f0e8" },
    [FX.Turret]: { color: "#ffcc66" },
    [FX.Smoke]: { color: "#737985" },
  },
};

function turretForNet(sim: Parameters<HeroModule["kit"]["tick"]>[0], net: number): number | undefined {
  if (net <= 0) return undefined;
  const e = sim.eidOf(net);
  return e !== undefined && sim.c.Construct.kind[e] === CK.Turret ? e : undefined;
}

function dist2d(sim: Parameters<HeroModule["kit"]["tick"]>[0], a: number, b: number) {
  const dx = sim.c.Pos.x[a]! - sim.c.Pos.x[b]!;
  const dz = sim.c.Pos.z[a]! - sim.c.Pos.z[b]!;
  return Math.hypot(dx, dz);
}

export default module;
